// TalkRobin: the "ring until opened" sound. Loud (boosted past normal
// volume, with a limiter so it doesn't crackle) and looping like a phone
// call. Browsers, phones especially, only allow sound after the person has
// tapped the page once, so the first tap or key press anywhere unlocks it.
const RINGTONE_URL = '/audio/dashboard/ringtone.mp3';
const BOOST = 2.5;
const UNLOCK_EVENTS = ['pointerdown', 'touchend', 'keydown'];

let context = null;
let ringtone = null; // decoded sound
let source = null; // the sound while it plays
let fallback = null; // plain <audio> where Web Audio isn't available
let wanted = false;

const AudioContextClass = () =>
  typeof window !== 'undefined' &&
  (window.AudioContext || window.webkitAudioContext);

const loadRingtone = async () => {
  if (!ringtone) {
    const response = await fetch(RINGTONE_URL);
    const data = await response.arrayBuffer();
    ringtone = await new Promise((resolve, reject) => {
      // Older Safari only supports the callback form.
      const result = context.decodeAudioData(data, resolve, reject);
      if (result?.then) result.then(resolve, reject);
    });
  }
  return ringtone;
};

const unlock = () => {
  const ContextClass = AudioContextClass();
  if (!ContextClass) return;
  try {
    context = context || new ContextClass();
    context.resume();
    // iOS unlocks only once something has played inside the tap.
    const silence = context.createBufferSource();
    silence.buffer = context.createBuffer(1, 1, 22050);
    silence.connect(context.destination);
    silence.start(0);
    loadRingtone().catch(() => {});
  } catch {
    // Web Audio unavailable: the <audio> fallback is used.
  }
  if (context?.state === 'running') {
    UNLOCK_EVENTS.forEach(name =>
      window.removeEventListener(name, unlock, true)
    );
  }
};

export const installRingerUnlock = () => {
  if (typeof window === 'undefined' || context?.state === 'running') return;
  UNLOCK_EVENTS.forEach(name =>
    window.addEventListener(name, unlock, { capture: true, passive: true })
  );
};

const playFallback = () => {
  fallback = fallback || new Audio(RINGTONE_URL);
  fallback.loop = true;
  fallback.volume = 1;
  return fallback.play();
};

// Resolves once the ring is audible; rejects (NotAllowedError) while the
// browser still blocks sound.
export const startRing = async () => {
  wanted = true;
  if (source || (fallback && !fallback.paused)) return;
  if (context && context.state !== 'running') {
    await context.resume().catch(() => {});
  }
  if (context?.state !== 'running') {
    await playFallback();
    if (!wanted) fallback.pause();
    return;
  }
  const sound = await loadRingtone();
  if (!wanted || source) return;
  source = context.createBufferSource();
  source.buffer = sound;
  source.loop = true;
  const gain = context.createGain();
  gain.gain.value = BOOST;
  const limiter = context.createDynamicsCompressor();
  limiter.threshold.value = -6;
  limiter.ratio.value = 20;
  source.connect(gain);
  gain.connect(limiter);
  limiter.connect(context.destination);
  source.start();
};

export const stopRing = () => {
  wanted = false;
  if (source) {
    try {
      source.stop();
    } catch {
      // already stopped
    }
    source.disconnect();
    source = null;
  }
  if (fallback) {
    fallback.pause();
    fallback.currentTime = 0;
  }
};
